// ─────────────────────────────────────────────────────────────────────────
// Payments-paused kill switch — the single source of truth for "we cannot take
// a card right now".
//
// WHY IT EXISTS: when our payment processor blocks the account, every checkout
// path in the app dead-ends silently — the buyer clicks Upgrade, gets bounced,
// and we lose both the sale and any record that they wanted one. This module
// turns that silent failure into an explicit, routed one: the click is
// intercepted at the last moment before Stripe, the buyer is told there is a
// temporary issue, and one button queues their request so support can put the
// purchase through by hand.
//
// WHERE IT IS READ:
//   • client — every pay handler calls `interceptIfPaymentsPaused(...)` first
//     (components/billing/payments-paused-store.ts) and returns early if true.
//   • server — every session-minting route early-returns `paymentsPausedJson()`
//     so a stale tab, a replayed fetch, or a surface we missed still cannot
//     reach Stripe.
//
// HOW IT LIFTS: `NEXT_PUBLIC_PAYMENTS_PAUSED=1` in the environment turns it on;
// unset (the default) and nothing in this file has any effect. Restoring
// payments is an env change + redeploy, NOT a code revert — which is what makes
// this safe to merge before it is needed.
//
// The NEXT_PUBLIC_ prefix is deliberate even though the server also reads it:
// the client needs it inlined at build time to decide whether to intercept, and
// a second server-only variable would be a second thing to remember to flip.
// Nothing here is a secret — the whole point is to tell the user.
// ─────────────────────────────────────────────────────────────────────────

/** Support address used only as the fallback when queueing a request fails. */
export const PAYMENTS_SUPPORT_EMAIL = "help@panda.new";

/**
 * Is the card-payment kill switch on?
 *
 * Reads the same variable on both client and server. On the client Next inlines
 * it at build time, so this is a constant comparison after bundling; on the
 * server it is read at request time.
 */
export function isPaymentsPaused(): boolean {
  return process.env.NEXT_PUBLIC_PAYMENTS_PAUSED === "1";
}

// ── What the buyer was trying to buy ─────────────────────────────────────────

/**
 * The kind of purchase that was intercepted. Drives the support-queue category
 * and (with `label`) the line the pop-up shows back to the buyer.
 */
export type PausedPurchaseKind = "upgrade" | "trial" | "topup";

/**
 * Everything the pop-up needs to name the blocked purchase back to the buyer
 * and hand it to the support queue intact.
 *
 * Every field but `kind` and `surface` is optional: a few entry points (a bare
 * card-setup click) genuinely have no amount, and a pop-up that names the
 * purchase vaguely still beats a redirect that fails. Pass what the call site
 * has.
 */
export interface PausedPurchaseIntent {
  kind: PausedPurchaseKind;
  /** Where the click came from, e.g. "upgrade_modal". Analytics + queue row. */
  surface: string;
  /** What they were buying, in the buyer's words — "Pro", "1,500 credits". */
  label?: string | null;
  /** Price as already formatted for display — "$29/mo", "$1", "$15". */
  priceLabel?: string | null;
  /** Plan / tier id, for analytics and the support queue. */
  plan?: string | null;
  cadence?: "monthly" | "annual" | null;
  credits?: number | null;
  amountUsd?: number | null;
}

// ── Copy ─────────────────────────────────────────────────────────────────────
//
// We do NOT name the processor, and we do not describe the problem. "We're
// experiencing a temporary issue" is all the buyer can act on; naming a vendor's
// outage on our own paywall reads as blame-shifting and stops being true the
// moment we route through anyone else.
//
// The second rule here is that every claim must be backed by something real.
// "We'll put yours through" and "you'll get an email" are only sayable because
// the proceed button writes a payment_requests row (migration 172) that support
// drains. If that write ever stops happening, this copy has to change with it —
// see PAYMENTS_PAUSED_COPY.saveFailed for the honest fallback when it does.

export const PAYMENTS_PAUSED_COPY = {
  /** Step 1 — shown the moment a pay click is intercepted. */
  title: "We're experiencing a temporary issue",
  body:
    "Payments are taking longer than usual to go through right now. Hit proceed " +
    "and we'll put yours through as soon as it clears — you'll get an email the " +
    "moment it's done.",
  /** Label above the named purchase. Present tense: it hasn't been lost. */
  intentLabel: "Your upgrade",
  intentLabelTopup: "Your top-up",
  intentLabelTrial: "Your trial",
  /** The buyer's first fear with delay framing is that money already left. */
  noCharge: "Nothing has been charged yet.",
  cta: "I understand — proceed",
  ctaBusy: "Saving…",
  dismiss: "Not now",

  /** Step 2 — shown once the request is safely queued. */
  queuedTitle: "You're in the queue",
  /** `{email}` is replaced with the signed-in address. */
  queuedBody:
    "We've saved your upgrade and we're on it. You'll get an email at {email} " +
    "the moment your plan is live — no need to do anything else.",
  queuedBodyNoEmail:
    "We've saved your upgrade and we're on it. You'll get an email the moment " +
    "your plan is live — no need to do anything else.",
  queuedIntentLabel: "Saved",
  queuedNoCharge: "You still haven't been charged.",
  queuedCta: "Back to building",
  queuedFinePrint: "Changed your mind? Reply to that email and we'll drop it.",

  /**
   * Step 1 failure — the queue write did not land (table missing, network, 500).
   *
   * This exists because the promise and the row are one thing: if nothing was
   * saved we must NOT show "you're in the queue", or we send the buyer away
   * believing they are handled when no record of them exists. Falling back to
   * the support address is the only claim still true at that point.
   */
  saveFailed: `We couldn't save that just now. Email ${PAYMENTS_SUPPORT_EMAIL} and we'll sort it out for you.`,
  saveFailedCta: `Email ${PAYMENTS_SUPPORT_EMAIL}`,

  /** Inline state on /checkout, where there was no click to intercept. */
  checkoutTitle: "We're experiencing a temporary issue",
  checkoutBody:
    "Payments are taking longer than usual to go through. Head back and try your " +
    "upgrade again — we'll take it from there.",
  checkoutCta: "Go back",
} as const;

/** The label above the named purchase, matched to what is being bought. */
export function pausedIntentLabel(kind: PausedPurchaseKind): string {
  switch (kind) {
    case "trial":
      return PAYMENTS_PAUSED_COPY.intentLabelTrial;
    case "topup":
      return PAYMENTS_PAUSED_COPY.intentLabelTopup;
    default:
      return PAYMENTS_PAUSED_COPY.intentLabel;
  }
}

/** Fill `{email}` in the queued-state body, or drop the clause if unknown. */
export function pausedQueuedBody(email?: string | null): string {
  if (!email) return PAYMENTS_PAUSED_COPY.queuedBodyNoEmail;
  return PAYMENTS_PAUSED_COPY.queuedBody.replace("{email}", email);
}

/**
 * Support mailto — the FALLBACK path only, used when the queue write fails.
 *
 * encodeURIComponent, NOT URLSearchParams: URLSearchParams is form encoding and
 * writes a space as "+", which a mail client drops into the body literally
 * (RFC 6068 has no such rule), so the draft opens reading "I'd+like+to+get+Pro".
 */
export function pausedSupportMailto(
  intent: PausedPurchaseIntent | null,
  accountEmail?: string | null,
): string {
  const what = [intent?.label, intent?.priceLabel].filter(Boolean).join(" — ");
  const lines: string[] = [
    what
      ? `I tried to ${intent?.kind === "topup" ? "buy" : "get"} ${what} but payments aren't going through.`
      : "I tried to upgrade but payments aren't going through.",
  ];
  if (accountEmail) lines.push(`Account: ${accountEmail}`);
  const ref = [intent?.kind, intent?.plan, intent?.cadence, intent?.credits]
    .filter(Boolean)
    .join(" · ");
  if (ref) lines.push(`Ref: ${ref}`);

  const subject = encodeURIComponent("Payment issue");
  const body = encodeURIComponent(lines.join("\n"));
  return `mailto:${PAYMENTS_SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
}

// ── Server guard ─────────────────────────────────────────────────────────────

/** Status used by every paused checkout route. 503 = temporary, retry later. */
export const PAYMENTS_PAUSED_STATUS = 503;

/**
 * The body every session-minting route returns while payments are paused.
 *
 * `error` carries a user-facing sentence because several call sites surface a
 * raw `error` string in a toast; `paymentsPaused` is the flag a caller should
 * branch on if it wants to open the pop-up instead.
 */
export function paymentsPausedJson(): {
  error: string;
  paymentsPaused: true;
} {
  return {
    error: `${PAYMENTS_PAUSED_COPY.title}. ${PAYMENTS_PAUSED_COPY.body}`,
    paymentsPaused: true,
  };
}
